import type { deCampaigns } from './de.campaigns';

export const enCampaigns: Record<keyof typeof deCampaigns, string> = {
  'nav.campaigns': 'Campaigns',

  'campaigns.title': 'Campaigns',
  'campaigns.empty': 'No campaigns yet. Create your first one.',
  'campaigns.create': 'Create campaign',
  'campaigns.name': 'Name',
  'campaigns.description': 'Description',
  'campaigns.open': 'Open',
  'campaigns.showArchived': 'Show archived',
  'campaigns.state.active': 'Running',
  'campaigns.state.stopped': 'Stopped',
  'campaigns.state.archived': 'Archived',
  'campaigns.mode.pool': 'Shared list',
  'campaigns.mode.assigned': 'Fixed allocation',
  'campaigns.contacts': 'Records',

  'campaign.tab.overview': 'Overview',
  'campaign.tab.contacts': 'Records',
  'campaign.tab.callers': 'Callers',
  'campaign.tab.statistics': 'Statistics',
  'campaign.tab.recordings': 'Recordings',
  'campaign.tab.settings': 'Settings',
  'campaign.comingSoon': 'This area will follow in one of the next steps.',
  'campaign.start': 'Start campaign',
  'campaign.stop': 'Stop campaign',
  'campaign.archive': 'Archive',
  'campaign.restore': 'Restore from archive',
  'campaign.delete': 'Delete campaign',
  'campaign.deleteWarning':
    'The campaign and its records will be removed from your view. This cannot be undone.',
  'campaign.stopFirst': 'Stop the campaign before changing this setting.',

  'campaign.overview.contacts': 'Records',
  'campaign.overview.callers': 'Released callers',
  'campaign.overview.mode': 'Way of working',
  'campaign.overview.state': 'Status',
  'campaign.overview.created': 'Created on',

  'campaign.settings.basics': 'Basics',
  'campaign.settings.mode': 'Way of working',
  'campaign.settings.modeHint':
    'Shared list: everyone works through the same list. Fixed allocation: each caller gets their own records.',
  'campaign.settings.changeMode': 'Change way of working',
  'campaign.settings.modePreview': 'This affects {contacts} records and {callers} callers. Continue?',
  'campaign.settings.country': 'Country of the phone numbers',
  'campaign.settings.countryHint': 'Numbers without a country code are completed with this country.',
  'campaign.settings.advanced': 'Advanced settings',
  'campaign.settings.parallelCalls': 'Simultaneous dial attempts',
  'campaign.settings.parallelMode': 'Counted',
  'campaign.settings.parallelMode.per_caller': 'per caller',
  'campaign.settings.parallelMode.global': 'in total',
  'campaign.settings.parallelWarning':
    'With this value people you call may briefly end up waiting on hold.',
  'campaign.settings.retry': 'Retries within 24 hours',
  'campaign.settings.recording': 'Record calls',
  'campaign.settings.mailbox': 'Detect answering machines',
  'campaign.settings.inbound': 'Accept incoming calls',
  'campaign.settings.queue': 'Hold queue',
  'campaign.settings.queueWait': 'Maximum waiting time in seconds',
  'campaign.settings.fields': 'Own fields',
  'campaign.settings.fieldsHint':
    'Additional details for your records. Fields created during an import also appear here.',
  'campaign.settings.fieldName': 'Field name',
  'campaign.settings.addField': 'Add field',
  'campaign.settings.required': 'Fill in before ending the call',
  'campaign.settings.removeField': 'Remove field',
  'campaign.settings.saved': 'Saved.',

  'campaign.callers.title': 'Released callers',
  'campaign.callers.hint': 'Only released callers see the records of this campaign.',
  'campaign.callers.add': 'Release caller',
  'campaign.callers.remove': 'Remove release',
  'campaign.callers.empty': 'Nobody has been released for this campaign yet.',
  'campaign.callers.assignedContacts': 'allocated records',
  'campaign.callers.removed': 'Release removed. {count} records are now allocated to nobody.',

  'contacts.title': 'Records',
  'contacts.search': 'Search',
  'contacts.searchHint': 'Number, name, note or record number',
  'contacts.empty': 'No records yet.',
  'contacts.noResults': 'No records match this search.',
  'contacts.create': 'Create record',
  'contacts.import': 'Import CSV',
  'contacts.export': 'Download as CSV',
  'contacts.total': '{count} records',
  'contacts.page': 'Page {page} of {pages}',
  'contacts.next': 'Next',
  'contacts.previous': 'Back',
  'contacts.filter.status': 'Status',
  'contacts.filter.all': 'All',
  'contacts.filter.assigned': 'Caller',
  'contacts.filter.archived': 'Archive',
  'contacts.filter.archived.active': 'Active',
  'contacts.filter.archived.archived': 'Archived',
  'contacts.filter.archived.all': 'All',
  'contacts.filter.callbackFrom': 'Callback from',
  'contacts.filter.callbackTo': 'Callback until',

  'contact.id': 'Record number',
  'contact.phone': 'Phone number',
  'contact.firstName': 'First name',
  'contact.lastName': 'Last name',
  'contact.note': 'Note',
  'contact.status': 'Status',
  'contact.callbackDate': 'Callback on',
  'contact.assignedTo': 'Allocated to',
  'contact.unassigned': 'Allocated to nobody',
  'contact.archive': 'Archive',
  'contact.restore': 'Restore',
  'contact.delete': 'Delete',
  'contact.deleteWarning': 'This record will be removed from your view.',
  'contact.saved': 'Saved.',
  'contact.archived': 'Archived',
  'contact.duplicateHint': 'This number already exists in record {id}.',
  'contact.requiredBeforeCall': 'To be filled in before ending the call',

  'status.new': 'New',
  'status.successful': 'Successful',
  'status.callback': 'Callback',
  'status.not_interested': 'Not interested',

  'import.title': 'Import CSV',
  'import.step.file': 'Choose file',
  'import.step.mapping': 'Match columns',
  'import.step.duplicates': 'Duplicate entries',
  'import.step.result': 'Result',
  'import.chooseFile': 'Choose CSV file',
  'import.fileHint': 'CSV files only, at most 20 MB.',
  'import.preview': 'Preview',
  'import.rows': 'rows in total',
  'import.validPhones': 'valid phone numbers',
  'import.invalidPhones': 'invalid phone numbers',
  'import.duplicates': 'duplicate numbers',
  'import.withId': 'rows with a record number',
  'import.mapping.phone': 'Column with the phone number',
  'import.mapping.firstName': 'Column with the first name',
  'import.mapping.lastName': 'Column with the last name',
  'import.mapping.none': '– do not import –',
  'import.mapping.hint': 'All remaining columns become own fields of the campaign.',
  'import.duplicates.question': 'What should happen with numbers that already exist?',
  'import.duplicates.skip': 'Leave existing records unchanged',
  'import.duplicates.overwrite': 'Complete existing records with the new details',
  'import.duplicates.overwriteHint': 'Empty columns do not overwrite existing details.',
  'import.start': 'Start import',
  'import.running': 'The import is running …',
  'import.result.created': 'newly created',
  'import.result.updated': 'updated',
  'import.result.skippedDuplicates': 'skipped (already present)',
  'import.result.skippedInvalid': 'skipped (faulty)',
  'import.result.createdFields': 'New fields',
  'import.result.errors': 'Faulty rows',
  'import.error.invalid_phone': 'Row {line}: phone number cannot be used',
  'import.error.missing_phone': 'Row {line}: no phone number',
  'import.error.unknown_public_id': 'Row {line}: unknown record number',
  'import.done': 'Done',

  'error.campaign.not_found': 'This campaign was not found.',
  'error.campaign.name_required': 'Please enter a name for the campaign.',
  'error.campaign.must_be_stopped': 'Stop the campaign before changing this setting.',
  'error.campaign.no_callers_assigned': 'Release at least one caller for the campaign first.',
  'error.campaign.archived_cannot_activate': 'An archived campaign cannot be started.',
  'error.campaign.not_released': 'This campaign has not been released for you.',
  'error.campaign.member_required': 'This caller has not been released for the campaign.',
  'error.campaign.member_not_found': 'This account has not been released for the campaign.',
  'error.campaign.parallel_out_of_range': 'Please choose a value between 1 and 5.',
  'error.campaign.invalid_country': 'This country is not available.',
  'error.contact.not_found': 'This record was not found.',
  'error.contact.invalid_phone': 'We cannot use this phone number. Please check your entry.',
  'error.contact.invalid_status': 'This status is not available.',
  'error.contact.invalid_callback_date': 'Please enter a valid date.',
  'error.contact.assignment_admin_only': 'Only an administrator can change the allocation.',
  'error.field.not_found': 'This field was not found.',
  'error.field.name_taken': 'A field with this name already exists.',
  'error.field.name_required': 'Please enter a name for the field.',
  'error.field.name_reserved': 'This name is already used by a fixed column. Please choose another one.',
  'error.import.file_required': 'Please choose a CSV file.',
  'error.import.empty_file': 'The file is empty.',
  'error.import.file_too_large': 'The file is too large. Please split the list into several files.',
  'error.import.not_found': 'The uploaded file is no longer available. Please upload it again.',
  'error.import.phone_column_required': 'Please choose the column with the phone number.',
};
